import { useCallback, useState } from 'react'

import { RequestStatus } from 'core/domain/Request'

import { type SpotQuote } from '../../domain/SpotQuote'
import { getSpotQuote } from '../../use-cases/getSpotQuote'
import { submitSpotQuote, type SubmitSpotQuoteParams } from '../../use-cases/submitSpotQuote'

type State = {
  spotQuote?: SpotQuote
  spotQuoteStatus: RequestStatus
}

const initialState = {
  spotQuoteStatus: RequestStatus.Initial,
}

export function useSpotQuote() {
  const [{ spotQuote, spotQuoteStatus }, setSpotQuoteState] = useState<State>(initialState)
  const [submitSpotQuoteStatus, setSubmitSpotQuoteStatus] = useState<RequestStatus>(
    RequestStatus.Initial
  )

  const fetchSpotQuote = useCallback(async (id: string, { signal }: AbortController) => {
    try {
      setSpotQuoteState(previous => ({ ...previous, spotQuoteStatus: RequestStatus.Pending }))

      const spotQuoteResult = await getSpotQuote(id, { signal })

      setSpotQuoteState(previous => ({
        ...previous,
        spotQuote: spotQuoteResult,
        spotQuoteStatus: RequestStatus.Done,
      }))
    } catch (error) {
      if (!signal.aborted) {
        setSpotQuoteState(previous => ({
          ...previous,
          spotQuoteStatus: RequestStatus.Error,
        }))
      }
    }
  }, [])

  const submitQuote = useCallback(async (draftQuote: SubmitSpotQuoteParams) => {
    setSubmitSpotQuoteStatus(RequestStatus.Pending)

    try {
      await submitSpotQuote(draftQuote)

      setSubmitSpotQuoteStatus(RequestStatus.Done)
    } catch (error) {
      setSubmitSpotQuoteStatus(RequestStatus.Error)

      throw error
    }
  }, [])

  return {
    spotQuote,
    spotQuoteStatus,
    submitSpotQuoteStatus,
    fetchSpotQuote,
    submitQuote,
  }
}
